import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { useSelector } from 'react-redux'

import type { AgentChatMessage, AgentChatSession } from '~/api/agentChat'
import { getUserSessions } from '~/api/agentChat'
import FeatureToggle from '~/constants/featureToggle'
import { useAgentChatAPI } from '~/hooks/useAgentChatAPI'
import { getCurrentUserId } from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'

export enum ChatMode {
  RAIL = 'rail',
  FLOATING = 'floating',
}

export interface FloatingPosition {
  x: number
  y: number
}

export interface FloatingSize {
  height: number
  width: number
}

const INITIAL_FLOATING_POSITION_Y = 72 // Below the header
const INITIAL_FLOATING_WIDTH = 500
const INITIAL_FLOATING_HEIGHT = 500
const SCROLLBAR_WIDTH = 16

interface AgentChatContextType {
  chatMode: ChatMode
  clearMessages: () => void
  closeChat: () => void
  connectionState: 'disconnected' | 'connecting' | 'connected' | 'error'
  createNewSession: () => Promise<void>
  error: string | null
  floatingPosition: FloatingPosition
  floatingSize: FloatingSize
  isChatOpen: boolean
  isLoading: boolean
  isLoadingSessions: boolean
  loadSessions: () => Promise<void>
  messages: AgentChatMessage[]
  openChat: () => void
  selectSession: (session: AgentChatSession) => Promise<void>
  sendMessage: (content: string) => Promise<void>
  session: AgentChatSession | null
  sessions: AgentChatSession[]
  setChatMode: (mode: ChatMode) => void
  startSession: () => Promise<void>
  toggleChat: () => void
  toggleChatMode: () => void
  updateFloatingPosition: (position: FloatingPosition) => void
  updateFloatingSize: (size: FloatingSize) => void
}

const AgentChatContext = createContext<AgentChatContextType | undefined>(
  undefined,
)

interface AgentChatProviderProps {
  children: ReactNode
}

export function AgentChatProvider({ children }: AgentChatProviderProps) {
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [chatMode, setChatMode] = useState<ChatMode>(ChatMode.FLOATING)
  const [sessions, setSessions] = useState<AgentChatSession[]>([])
  const [isLoadingSessions, setIsLoadingSessions] = useState(false)
  const [floatingPosition, setFloatingPosition] = useState<FloatingPosition>({
    x:
      typeof window !== 'undefined'
        ? window.innerWidth - INITIAL_FLOATING_WIDTH - SCROLLBAR_WIDTH
        : 400,
    y: INITIAL_FLOATING_POSITION_Y,
  })
  const [floatingSize, setFloatingSize] = useState<FloatingSize>({
    width: INITIAL_FLOATING_WIDTH,
    height:
      typeof window !== 'undefined'
        ? window.innerHeight - INITIAL_FLOATING_POSITION_Y * 2
        : INITIAL_FLOATING_HEIGHT,
  })

  const isAgentChatEnabled = useSelector(
    getFeatureToggle(FeatureToggle.AGENT_CHAT),
  )
  const currentUserId = useSelector(getCurrentUserId)

  const updateSession = useCallback((updatedSession: AgentChatSession) => {
    setSessions((prev) =>
      prev.map((s) => (s.id === updatedSession.id ? updatedSession : s)),
    )
  }, [])

  const addSession = useCallback((newSession: AgentChatSession) => {
    setSessions((prev) => [newSession, ...prev])
  }, [])

  const {
    messages,
    loading: isLoading,
    connectionState,
    sendMessage: hookSendMessage,
    startSession: hookStartSession,
    clearMessages,
    error,
    loadSession: hookLoadSession,
    session,
  } = useAgentChatAPI({
    onSessionUpdate: updateSession,
    onSessionCreated: addSession,
  })

  const openChat = useCallback(() => {
    setIsChatOpen(true)
  }, [])

  const closeChat = useCallback(() => {
    setIsChatOpen(false)
  }, [])

  const toggleChat = useCallback(() => {
    setIsChatOpen((prev) => !prev)
  }, [])

  const updateFloatingPosition = useCallback((position: FloatingPosition) => {
    setFloatingPosition(position)
  }, [])

  const updateFloatingSize = useCallback((size: FloatingSize) => {
    setFloatingSize(size)
  }, [])

  const toggleChatMode = useCallback(() => {
    setChatMode((prev) =>
      prev === ChatMode.RAIL ? ChatMode.FLOATING : ChatMode.RAIL,
    )
  }, [])

  const loadSessions = useCallback(async () => {
    if (!currentUserId) return

    try {
      setIsLoadingSessions(true)
      const userSessions = await getUserSessions(currentUserId)
      setSessions(userSessions)
    } catch (caughtError) {
      // eslint-disable-next-line no-console
      console.error('Failed to load sessions:', caughtError)
    } finally {
      setIsLoadingSessions(false)
    }
  }, [currentUserId])

  const selectSession = useCallback(
    async (selectedSession: AgentChatSession) => {
      try {
        await hookLoadSession(selectedSession.id)
      } catch (caughtError) {
        // eslint-disable-next-line no-console
        console.error('Failed to select session:', caughtError)
      }
    },
    [hookLoadSession],
  )

  const createNewSession = useCallback(async () => {
    try {
      await hookStartSession()
    } catch (caughtError) {
      // eslint-disable-next-line no-console
      console.error('Failed to create new session:', caughtError)
    }
  }, [hookStartSession])

  // Auto-load most recent session when chat opens
  useEffect(() => {
    if (isAgentChatEnabled && currentUserId) {
      const autoLoadMostRecentSession = async () => {
        try {
          const userSessions = await getUserSessions(currentUserId)
          setSessions(userSessions)

          if (userSessions && userSessions.length > 0) {
            // Sort by most recent and load the first one
            const sortedSessions = [...userSessions].sort(
              (a, b) => b.lastUpdateTime - a.lastUpdateTime,
            )
            if (!session) {
              await hookLoadSession(sortedSessions[0].id)
            }
          }
        } catch (caughtError) {
          // eslint-disable-next-line no-console
          console.error('Failed to auto-load most recent session:', caughtError)
        }
      }

      autoLoadMostRecentSession()
    }
  }, [isAgentChatEnabled, currentUserId, isChatOpen])

  useEffect(() => {
    if (isChatOpen && isAgentChatEnabled && chatMode === ChatMode.RAIL) {
      document.body.classList.add('agent-chat-rail-open')
    } else {
      document.body.classList.remove('agent-chat-rail-open')
    }

    return () => {
      document.body.classList.remove('agent-chat-rail-open')
    }
  }, [isChatOpen, isAgentChatEnabled, chatMode])

  const value = React.useMemo<AgentChatContextType>(
    () => ({
      isChatOpen,
      openChat,
      closeChat,
      toggleChat,
      chatMode,
      setChatMode,
      toggleChatMode,
      floatingPosition,
      updateFloatingPosition,
      floatingSize,
      updateFloatingSize,
      messages,
      isLoading,
      isLoadingSessions,
      connectionState,
      sendMessage: hookSendMessage,
      startSession: hookStartSession,
      clearMessages,
      error,
      session,
      sessions,
      loadSessions,
      selectSession,
      createNewSession,
    }),
    [
      isChatOpen,
      openChat,
      closeChat,
      toggleChat,
      chatMode,
      setChatMode,
      toggleChatMode,
      floatingPosition,
      updateFloatingPosition,
      floatingSize,
      updateFloatingSize,
      messages,
      isLoading,
      isLoadingSessions,
      connectionState,
      hookSendMessage,
      hookStartSession,
      clearMessages,
      error,
      session,
      sessions,
      loadSessions,
      selectSession,
      createNewSession,
    ],
  )

  return (
    <AgentChatContext.Provider value={value}>
      {children}
    </AgentChatContext.Provider>
  )
}

export function useAgentChat() {
  const context = useContext(AgentChatContext)
  if (context === undefined) {
    throw new Error('useAgentChat must be used within an AgentChatProvider')
  }
  return context
}
